// React and React Native Core imports
import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

// 3rd party libraries
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';

// Our Own imports
import FeedFloErrorBoundary from '../../organisms/FeedFloErrorBoundary';
import Page from '../../atoms/Page';
import FeedFloTabs from '../../atoms/FeedFloTabs';
import Heading from '../../atoms/Heading';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import { Privilege, useCan } from '@feedflo/access-control';
import Combobox from '../../atoms/Combobox';
import UserDetailPage from '../../pages/UserDetailPage';
import OrgTierSettingsPage from '../OrgTierSettingsPage/OrgTierSettingsPage';

// Relative path imports
import './OrgManagementPage.scss';
import OrgBarnsTab from './OrgBarnsTab';
import OrgUsersTab from './OrgUsersTab';
import { ORGANIZATION_GQL, ORG_LIST_QUERY } from './queries';

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};
function OrgManagementPage({ className = '', tabsClassName = '', organizationIdOverride = null }) {
  // --- State hooks ---
  const [activeOrgId, setActiveOrgId] = useState(organizationIdOverride);

  // --- Atoms and other Hooks ---
  const navigate = useNavigate();
  const { censor } = useCensor();
  const { can, loading: canLoading, privileges } = useCan();

  // --- Route params ---
  const { id: routeOrgId } = useParams();

  // --- Apollo/GraphQL hooks ---
  const {
    loading,
    error,
    data: orgData,
  } = useQuery(ORGANIZATION_GQL, {
    variables: { organization_id: activeOrgId },
    skip: !activeOrgId,
    context: asUserContext,
  });

  const {
    loading: orgListLoading,
    error: orgListError,
    data: orgListData,
  } = useQuery(ORG_LIST_QUERY, {
    context: asUserContext,
  });

  // --- Derived state (includes some useMemos)---
  const organization = useMemo(() => {
    if (!orgData || loading) {
      return null;
    }

    return {
      id: orgData?.organization?.id,
      name: orgData?.organization?.name ? censor(orgData?.organization?.name, censorTypes.organization) : '',
    };
  }, [orgData, loading, censor]);

  const orgListCensored = useMemo(() => {
    if (orgListLoading || orgListError || !orgListData || canLoading) {
      return [];
    }
    return orgListData?.organization
      ?.map((o) => {
        return {
          id: o.id,
          name: censor(o.name, 'organization'),
        };
      })
      .filter(
        (o) =>
          can('org', o?.id, Privilege.BARN_UPDATE) ||
          can('org', o?.id, Privilege.BARN_USERS_MANAGE) ||
          can('org', o?.id, Privilege.BARN_VIEW) ||
          can('org', o?.id, Privilege.ORG_BARN_CREATE) ||
          can('org', o?.id, Privilege.ORG_GENERAL_ADMINISTRATE) ||
          can('org', o?.id, Privilege.ORG_USER_VIEW) ||
          can('org', o?.id, Privilege.ORG_USERS_MANAGE) ||
          can('org', o?.id, Privilege.USER_ACCESS_MANAGE),
      );
  }, [orgListData, orgListLoading, orgListError, canLoading, censor, can]);

  const hasMultipleOrgs = orgListCensored.length > 1;

  const tabs = useMemo(() => {
    if (canLoading || !privileges || !organization?.id) {
      return null;
    }

    const canViewBarnsTab =
      can('org', organization?.id, Privilege.ORG_GENERAL_ADMINISTRATE) ||
      can('org', organization?.id, Privilege.ORG_BARN_CREATE) ||
      can('org', orgData?.organization?.id, Privilege.BARN_UPDATE) ||
      can('org', organization?.id, Privilege.BARN_VIEW);
    const canViewUsersTab = can('org', organization?.id, Privilege.ORG_USER_VIEW);
    const canViewTierSettingsTab = can('org', organization?.id, Privilege.ORG_GENERAL_ADMINISTRATE);

    const tabArray = [];

    if (canViewBarnsTab) {
      tabArray.push({
        title: 'Barns',
        link: 'barns',
      });
    }
    if (canViewUsersTab) {
      tabArray.push({
        title: 'Users',
        link: `users`,
      });
    }
    if (canViewTierSettingsTab) {
      tabArray.push({
        title: 'Notification Policies',
        link: 'notifications/settings',
      });
    }

    return tabArray;
  }, [canLoading, privileges, can, organization?.id]);

  useEffect(() => {
    if (canLoading || !privileges || orgListLoading) return; // not ready yet

    // Each of these checks to make sure it's not setting the same value again
    if (routeOrgId && routeOrgId !== activeOrgId && uuidRegex.test(routeOrgId)) {
      // from route params
      setActiveOrgId(routeOrgId);
    } else if (
      orgListData?.organization?.length === 1 &&
      !hasMultipleOrgs &&
      !orgListLoading &&
      !orgListError &&
      orgListData?.organization?.[0]?.id !== activeOrgId
    ) {
      // users with only access to a single org
      const usersOnlyOrgId = orgListData?.organization?.[0]?.id;
      setActiveOrgId(usersOnlyOrgId);
      navigate(`/orgs/${usersOnlyOrgId}`);
    } else if (activeOrgId === null) {
      // explicitly set to false to indicate things are loaded and there is no active org
      setActiveOrgId(false);
    } else if (!routeOrgId && activeOrgId) {
      // explicitly set to false if there's no org ID in the route _but_ there's an activeOrgId
      // happens when clicking the sidebar link
      setActiveOrgId(false);
    }
  }, [routeOrgId, organizationIdOverride, orgListLoading, orgListError, orgListData, canLoading]);

  const pageTitleSegments = useMemo(() => {
    const segments = [];

    if (organization?.name) {
      segments.push(organization?.name);
    }

    return segments;
  }, [organization, censor]);

  const IdWrappedOrgName = withStaffOnlyIdWrapper(organization?.name ? organization?.name : <LoadingSkeleton />);
  const IdWrappedOrgPicker = withStaffOnlyIdWrapper(<></>);

  const pageDisplayHeader = (
    <div>
      <Heading className="OrgManagementPage-Header">
        {hasMultipleOrgs ? (
          <div className="OrgManagementPage-orgPickerWrapper">
            <Combobox
              className="OrgManagementPage-orgPicker"
              inputRowClassName="OrgManagementPage-orgPickerInputRow"
              inputClassName="OrgManagementPage-orgPickerInput"
              placeholder="Select Organization"
              listItemClassName="OrgManagementPage-orgPickerlistItem"
              items={orgListCensored}
              preSelectedItem={organization}
              clearInputOnFocus
              onChange={(x) => {
                const pathSegments = window.location.pathname.split('/').filter(Boolean);
                const targetTab = pathSegments.slice(2).join('/');

                if (activeOrgId !== x?.id) {
                  setActiveOrgId(x?.id);

                  navigate(`/orgs/${x?.id}${targetTab ? `/${targetTab}` : ''}`);
                }
              }}
            />
            {activeOrgId && <IdWrappedOrgPicker data={activeOrgId} />}
          </div>
        ) : (
          <IdWrappedOrgName data={activeOrgId} hoverText="Copy Organization ID" />
        )}
      </Heading>
    </div>
  );

  if (error) throw error;

  // Show loading skeleton for tabs while privileges and queries are loading
  if (canLoading || loading || orgListLoading || (tabs === null && activeOrgId !== false)) {
    return (
      <Page className={`OrgManagementPage ${className}`} headerComponent={pageDisplayHeader}>
        <div className={`FeedFloTabs ${tabsClassName}`}>
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="FeedFloTabs-tab">
              <LoadingSkeleton className="FeedFloTabs-tab-skeleton" style={{ width: '80px', height: '24px' }} />
              <div className="spacer" />
            </div>
          ))}
          <div className="grower" />
        </div>
        <div className="OrgManagementPage-content-skeleton">
          <LoadingSkeleton
            className="OrgManagementPage-content-skeleton-item"
            style={{ width: '100%', height: '400px' }}
          />
        </div>
      </Page>
    );
  }

  return (
    <Page className={`OrgManagementPage ${className}`} headerComponent={pageDisplayHeader}>
      <FeedFloTabs tabData={tabs || []} className={tabsClassName} />
      {!organization ? (
        <div>Select an Organization</div>
      ) : (
        <FeedFloErrorBoundary>
          <Routes>
            <Route path="users/:id" element={<UserDetailPage hidePageTitleText={true} orgId={routeOrgId} />} />
            <Route
              path="users"
              element={
                <OrgUsersTab
                  loading={loading}
                  titleSegments={pageTitleSegments}
                  organizationId={activeOrgId}
                  organization={organization}
                />
              }
            />
            <Route
              path="barns"
              element={
                <OrgBarnsTab
                  loading={loading}
                  titleSegments={pageTitleSegments}
                  organizationId={activeOrgId}
                  organization={organization}
                />
              }
            />
            <Route path="overview" element={<Navigate to={`/orgs/${activeOrgId}/barns`} replace />} />
            <Route path="notifications/settings" element={<OrgTierSettingsPage titleSegments={pageTitleSegments} />} />
            <Route
              path=""
              element={
                <Navigate
                  to={tabs.length > 0 ? tabs[0].link : ''} // default to '' for when there's no org
                  replace={true}
                />
              }
            />
          </Routes>
        </FeedFloErrorBoundary>
      )}
    </Page>
  );
}

OrgManagementPage.propTypes = {
  className: PropTypes.string,
  tabsClassName: PropTypes.string,
  organizationIdOverride: PropTypes.string,
};

export default OrgManagementPage;
